import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '../api/auth'
import type { AuthResponse } from '../api/auth'

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(localStorage.getItem('token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'))
  const user = ref<AuthResponse['user'] | null>(null)
  const loading = ref(false)
  const tokenExpiresAt = ref<number | null>(
    localStorage.getItem('tokenExpiresAt') 
      ? parseInt(localStorage.getItem('tokenExpiresAt')!) 
      : null
  )

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userPlan = computed(() => user.value?.plan || 'free')

  // Actions
  const setAuth = (authData: AuthResponse) => {
    token.value = authData.token
    refreshToken.value = authData.refreshToken
    user.value = authData.user
    // 计算过期时间（当前时间 + expiresIn秒）
    const expiresAt = Date.now() + authData.expiresIn * 1000
    tokenExpiresAt.value = expiresAt
    
    localStorage.setItem('token', authData.token)
    localStorage.setItem('refreshToken', authData.refreshToken)
    localStorage.setItem('user', JSON.stringify(authData.user))
    localStorage.setItem('tokenExpiresAt', expiresAt.toString())
  }

  const updateToken = (newToken: string, expiresIn: number) => {
    token.value = newToken
    const expiresAt = Date.now() + expiresIn * 1000
    tokenExpiresAt.value = expiresAt
    localStorage.setItem('token', newToken)
    localStorage.setItem('tokenExpiresAt', expiresAt.toString())
  }

  const clearAuth = () => {
    token.value = null
    refreshToken.value = null
    user.value = null
    tokenExpiresAt.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('tokenExpiresAt')
  }

  const initializeAuth = () => {
    const storedToken = localStorage.getItem('token')
    const storedRefreshToken = localStorage.getItem('refreshToken')
    const storedUser = localStorage.getItem('user')
    const storedExpiresAt = localStorage.getItem('tokenExpiresAt')
    
    if (storedToken && storedRefreshToken && storedUser) {
      token.value = storedToken
      refreshToken.value = storedRefreshToken
      tokenExpiresAt.value = storedExpiresAt ? parseInt(storedExpiresAt) : null
      try {
        user.value = JSON.parse(storedUser)
      } catch {
        clearAuth()
      }
    }
  }

  // 检查 token 是否需要刷新（过期前5分钟）
  const shouldRefreshToken = (): boolean => {
    if (!tokenExpiresAt.value) return false
    const refreshThreshold = 5 * 60 * 1000 // 5分钟
    return Date.now() + refreshThreshold >= tokenExpiresAt.value
  }

  // 刷新 token
  const refreshAccessToken = async (): Promise<boolean> => {
    if (!refreshToken.value) return false
    
    try {
      const response = await authApi.refresh(refreshToken.value)
      updateToken(response.token, response.expiresIn)
      return true
    } catch (error) {
      // 刷新失败，清除登录状态
      clearAuth()
      return false
    }
  }

  const login = async (email: string, password: string) => {
    loading.value = true
    try {
      const response = await authApi.login({ email, password })
      setAuth(response)
      return response
    } finally {
      loading.value = false
    }
  }

  const register = async (name: string, email: string, password: string) => {
    loading.value = true
    try {
      const response = await authApi.register({ name, email, password })
      setAuth(response)
      return response
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } finally {
      clearAuth()
    }
  }

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe()
      user.value = userData
      localStorage.setItem('user', JSON.stringify(userData))
    } catch {
      clearAuth()
    }
  }

  return {
    token,
    refreshToken,
    user,
    loading,
    isAuthenticated,
    userPlan,
    login,
    register,
    logout,
    fetchUser,
    initializeAuth,
    setAuth,
    clearAuth,
    shouldRefreshToken,
    refreshAccessToken,
  }
})
